import { computed, effect, Injectable, signal } from '@angular/core';

import {
    BroadcastChannelMessageEnum,
    BroadcastChannelNameEnum,
    BroadcastChannelSourceEnum,
    SelectedEntitiesByLayerIdBroadcastChannelData,
} from '@connect-services/client/models/broadcast-channel-data.model';
import { LayerStyleTypeEnum } from '@connect-services/client/models/highlight-entity.model';
import { AssertService } from '@connect-services/client/services/assert.service';
import { SESSION_STORAGE_SELECTED_ENTITIES_BY_LAYER_ID_KEY } from '@connect-services/client/services/map/selection/selection.service';

export type SelectedEntitiesByLayerId = Record<string, SelectedEntity[]>;

export type SelectedEntity = Record<string, unknown> & {
    isInCreationState: boolean;
    isVisible: boolean;
    styleType?: LayerStyleTypeEnum;
    uuid: string;
};

@Injectable({
    providedIn: 'root',
})
export class SelectionStateService {
    public readonly hasSelectedEntities = computed(() => {
        const byLayerId = this._selectedEntitiesByLayerId();

        return Object.values(byLayerId).some((entities: SelectedEntity[]) => entities.length > 0);
    });

    public readonly isEditingSelection = computed(() => {
        return this.isEditModeActive() && this.hasSelectedEntities();
    });

    public readonly selectedEntitiesByLayerId = computed<SelectedEntitiesByLayerId>((): SelectedEntitiesByLayerId => {
        return this._selectedEntitiesByLayerId();
    });
    public readonly selectedEntitiesCount = computed<number>(() => {
        return Object.values(this._selectedEntitiesByLayerId()).reduce(
            (total: number, entities: SelectedEntity[]) => total + entities.length,
            0,
        );
    });
    public readonly selectedLayerCount = computed<number>(() => {
        return Object.keys(this._selectedEntitiesByLayerId()).length;
    });

    private readonly _selectedEntitiesByLayerId = signal<SelectedEntitiesByLayerId>({});
    private readonly isApplyingExternalSelectionSync = signal(false);
    private readonly isEditModeActive = signal<boolean>(false);
    private readonly layerBroadcastChannel = new BroadcastChannel(BroadcastChannelNameEnum.LAYERS);

    public constructor() {
        this.restoreSelectedEntitiesAndLayersFromSessionStorage();
        this.listenLayersAndEntitiesSelectionBroadcast();

        effect(() => {
            this.synchronizeSelectedEntitiesToSessionStorage(
                this.selectedEntitiesByLayerId(),
                this.isApplyingExternalSelectionSync(),
            );
        });
    }

    public clearEntities(): void {
        this._selectedEntitiesByLayerId.update((previous: SelectedEntitiesByLayerId) => {
            return Object.fromEntries(
                Object.keys(previous).map((layerId: string) => {
                    return [layerId, []];
                }),
            );
        });
    }

    public clearEntitiesAndLayer(): void {
        this._selectedEntitiesByLayerId.set({});
    }

    public restoreSelectedEntitiesAndLayersFromSessionStorage(): void {
        const storedValue: string | null = sessionStorage.getItem(SESSION_STORAGE_SELECTED_ENTITIES_BY_LAYER_ID_KEY);

        if (!storedValue) {
            return;
        }

        this.storeSelectedEntities(JSON.parse(storedValue));
    }

    public setEditModeActive(isActive: boolean): void {
        if (this.isEditModeActive() === isActive) {
            return;
        }

        this.isEditModeActive.set(isActive);
    }

    public storeSelectedEntities(entitiesByLayerId: SelectedEntitiesByLayerId): void {
        const current = this._selectedEntitiesByLayerId();

        if (JSON.stringify(current) === JSON.stringify(entitiesByLayerId)) {
            return;
        }

        this._selectedEntitiesByLayerId.set(entitiesByLayerId);
    }

    public updateSelectedEntitiesByLayerId(
        updater: (previous: SelectedEntitiesByLayerId) => SelectedEntitiesByLayerId,
    ): void {
        this._selectedEntitiesByLayerId.update(updater);
    }

    private listenLayersAndEntitiesSelectionBroadcast(): void {
        this.layerBroadcastChannel.onmessage = (
            event: MessageEvent<SelectedEntitiesByLayerIdBroadcastChannelData>,
        ): void => {
            try {
                AssertService.assertBroadcastChannelEvent(event.data);

                if (event.data.message !== BroadcastChannelMessageEnum.SELECTED_ENTITIES_BY_LAYER_ID) {
                    return;
                }

                AssertService.assertSelectedEntitiesByLayerId(event.data.additionalData);
            } catch {
                return;
            }

            const currentSerializedValue = JSON.stringify(this.selectedEntitiesByLayerId());
            const nextSerializedValue = JSON.stringify(event.data.additionalData);

            if (currentSerializedValue === nextSerializedValue) {
                return;
            }

            this.isApplyingExternalSelectionSync.set(true);

            this.storeSelectedEntities(event.data.additionalData);
        };
    }

    private synchronizeSelectedEntitiesToSessionStorage(
        selectedEntitiesByLayerId: SelectedEntitiesByLayerId,
        isApplyingExternalSelection: boolean,
    ): void {
        sessionStorage.setItem(
            SESSION_STORAGE_SELECTED_ENTITIES_BY_LAYER_ID_KEY,
            JSON.stringify(selectedEntitiesByLayerId),
        );

        if (isApplyingExternalSelection) {
            this.isApplyingExternalSelectionSync.set(false);

            return;
        }

        this.layerBroadcastChannel.postMessage({
            additionalData: selectedEntitiesByLayerId,
            message: BroadcastChannelMessageEnum.SELECTED_ENTITIES_BY_LAYER_ID,
            source: BroadcastChannelSourceEnum.TABLE_VIEW,
        });
    }
}
